import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Typography,
  Chip,
  styled,
  IconButton,
  Box,
  Paper,
  Rating,
  Collapse,
  useMediaQuery,
  ThemeProvider,
  createTheme
} from '@mui/material';
import {
  Close,
  ExpandMore,
  ExpandLess,
  Home,
  SquareFoot,
  Bed,
  Bathtub,
  Garage
} from '@mui/icons-material';

// Paleta de cores do Branding
const colors = {
  primary: '#5AB9D3',       // Azul claro principal
  primaryLight: '#6AC9E3',  // Azul claro mais claro
  primaryDark: '#4AA5C0',   // Azul claro mais escuro
  secondary: '#101A3A',     // Azul escuro principal
  secondaryLight: '#1A2747', // Azul escuro mais claro
  white: '#FFFFFF',         // Branco puro
  textLight: '#E2E8F0',     // Branco acinzentado claro
  accent: '#38A169',        // Verde para destaques
  error: '#E53E3E',         // Vermelho para erros
  success: '#38A169'        // Verde para sucesso
};

// Create theme with the color palette
const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: colors.white
    },
    secondary: {
      main: colors.secondary,
      light: colors.secondaryLight
    },
    error: {
      main: colors.error
    },
    success: {
      main: colors.success,
      dark: colors.accent
    },
    text: {
      primary: colors.white,
      secondary: colors.textLight,
      light: colors.textLight
    },
    background: {
      default: colors.secondary,
      paper: colors.secondaryLight
    },
    grey: {
      50: 'rgba(255, 255, 255, 0.05)',
      100: 'rgba(255, 255, 255, 0.1)',
      200: 'rgba(255, 255, 255, 0.2)',
      300: 'rgba(255, 255, 255, 0.3)',
    }
  },
  shape: {
    borderRadius: 8
  },
  spacing: 8,
});

const PremiumDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 3,
    padding: 0,
    minWidth: '650px',
    maxWidth: '800px',
    overflow: 'hidden',
    boxShadow: theme.shadows[10],
    background: `linear-gradient(160deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
      margin: theme.spacing(1),
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '100%',
      height: '100%',
      background: `radial-gradient(circle at 80% 20%, rgba(90, 185, 211, 0.15) 0%, transparent 70%)`,
      pointerEvents: 'none',
      zIndex: 0,
    },
  },
  '& .MuiDialogContent-root': {
    position: 'relative',
    zIndex: 1,
  },
}));

const PremiumDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(160deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  padding: theme.spacing(2, 3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  },
}));

const DetailSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(2),
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StatusBadge = styled(Chip)(({ status, theme }) => ({
  backgroundColor: status ? theme.palette.success.dark : theme.palette.error.dark,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5),
  fontSize: '0.75rem',
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  borderRadius: '50%',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
}));

// Função para formatar moeda em Euro
const formatCurrency = (value) => {
  if (value === undefined || value === null) return 'N/A';
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Função para formatar tipos de propriedade
const formatPropertyType = (type) => {
  const types = {
    apartment: 'Apartamento',
    house: 'Casa',
    villa: 'Villa',
    land: 'Terreno',
    commercial: 'Comercial',
    other: 'Outro'
  };
  return types[type] || type;
};

const ImovelDetailDialog = ({ 
  open, 
  onClose, 
  imovel
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedSections, setExpandedSections] = useState({
    description: false,
    features: true,
    address: false
  });

  if (!imovel) return null;

  const { 
    title, 
    description, 
    type, 
    price, 
    squareMeters, 
    typology, 
    status,
    address,
    features,
    rating
  } = imovel;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderBasicInfo = () => (
    <Box>
      <DetailSectionTitle variant="h6">
        <Home fontSize="small" /> Informações Básicas
      </DetailSectionTitle>
      <Divider sx={{ mb: 2, borderColor: theme.palette.grey[300] }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Título" 
                secondary={title || 'N/A'} 
                primaryTypographyProps={{ color: theme.palette.text.primary }}
                secondaryTypographyProps={{ 
                  sx: { fontWeight: 'medium' },
                  color: theme.palette.text.secondary
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Tipo" 
                secondary={formatPropertyType(type) || 'N/A'} 
                primaryTypographyProps={{ color: theme.palette.text.primary }}
                secondaryTypographyProps={{ 
                  sx: { fontWeight: 'medium' },
                  color: theme.palette.text.secondary
                }}
              />
            </ListItem>
          </List>
        </Grid>
        <Grid item xs={12} sm={6}>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Status" 
                primaryTypographyProps={{ color: theme.palette.text.primary }}
                secondary={
                  <StatusBadge 
                    label={status ? 'Ativo' : 'Inativo'} 
                    status={status} 
                  />
                } 
              />
            </ListItem>
            {rating && (
              <ListItem>
                <ListItemText 
                  primary="Avaliação" 
                  primaryTypographyProps={{ color: theme.palette.text.primary }}
                  secondary={
                    <Box display="flex" alignItems="center">
                      <Rating value={rating} precision={0.5} readOnly size="small" />
                      <Typography variant="body2" sx={{ ml: 1, color: theme.palette.text.secondary }}>
                        {rating.toFixed(1)}
                      </Typography>
                    </Box>
                  } 
                />
              </ListItem>
            )}
          </List>
        </Grid>
      </Grid>
    </Box>
  );

  const renderDescription = () => (
    <Box>
      <DetailSectionTitle variant="h6">
        Descrição
        <IconButton 
          size="small" 
          onClick={() => toggleSection('description')}
          sx={{ ml: 'auto', color: theme.palette.primary.main }}
        >
          {expandedSections.description ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </DetailSectionTitle>
      <Divider sx={{ mb: 2, borderColor: theme.palette.grey[300] }} />
      <Collapse in={expandedSections.description}>
        <Paper elevation={0} sx={{ 
          p: 2, 
          backgroundColor: theme.palette.grey[50],
          color: theme.palette.text.primary
        }}>
          <Typography variant="body2">
            {description || 'Nenhuma descrição disponível'}
          </Typography>
        </Paper>
      </Collapse>
    </Box>
  );

  const renderValues = () => (
    <Box>
      <DetailSectionTitle variant="h6">
        <SquareFoot fontSize="small" /> Valores e Dimensões
      </DetailSectionTitle>
      <Divider sx={{ mb: 2, borderColor: theme.palette.grey[300] }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ 
            p: 2, 
            textAlign: 'center', 
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText
          }}>
            <Typography variant="subtitle2">
              Preço
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatCurrency(price) || 'N/A'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ 
            p: 2, 
            textAlign: 'center', 
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText
          }}>
            <Typography variant="subtitle2">
              Área
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {squareMeters || 'N/A'} m²
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ 
            p: 2, 
            textAlign: 'center', 
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText
          }}>
            <Typography variant="subtitle2">
              Quartos
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {typology || 'N/A'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderAddress = () => (
    <Box>
      <DetailSectionTitle variant="h6">
        Endereço
        <IconButton 
          size="small" 
          onClick={() => toggleSection('address')}
          sx={{ ml: 'auto', color: theme.palette.primary.main }}
        >
          {expandedSections.address ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </DetailSectionTitle>
      <Divider sx={{ mb: 2, borderColor: theme.palette.grey[300] }} />
      <Collapse in={expandedSections.address}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Logradouro" 
                  primaryTypographyProps={{ color: theme.palette.text.primary }}
                  secondary={`${address?.street || 'N/A'}, ${address?.number || ''}`} 
                  secondaryTypographyProps={{ 
                    sx: { fontWeight: 'medium' },
                    color: theme.palette.text.secondary
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Complemento" 
                  primaryTypographyProps={{ color: theme.palette.text.primary }}
                  secondary={address?.complement || 'N/A'} 
                  secondaryTypographyProps={{ 
                    sx: { fontWeight: 'medium' },
                    color: theme.palette.text.secondary
                  }}
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} sm={6}>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Bairro" 
                  primaryTypographyProps={{ color: theme.palette.text.primary }}
                  secondary={address?.neighborhood || 'N/A'} 
                  secondaryTypographyProps={{ 
                    sx: { fontWeight: 'medium' },
                    color: theme.palette.text.secondary
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Cidade/Estado" 
                  primaryTypographyProps={{ color: theme.palette.text.primary }}
                  secondary={`${address?.city || 'N/A'}/${address?.state || 'N/A'}`} 
                  secondaryTypographyProps={{ 
                    sx: { fontWeight: 'medium' },
                    color: theme.palette.text.secondary
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="CEP" 
                  primaryTypographyProps={{ color: theme.palette.text.primary }}
                  secondary={address?.zipCode || 'N/A'} 
                  secondaryTypographyProps={{ 
                    sx: { fontWeight: 'medium' },
                    color: theme.palette.text.secondary
                  }}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );

  const renderFeatures = () => {
    if (!features || features.length === 0) return null;

    return (
      <Box>
        <DetailSectionTitle variant="h6">
          Características
          <IconButton 
            size="small" 
            onClick={() => toggleSection('features')}
            sx={{ ml: 'auto', color: theme.palette.primary.main }}
          >
            {expandedSections.features ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </DetailSectionTitle>
        <Divider sx={{ mb: 2, borderColor: theme.palette.grey[300] }} />
        <Collapse in={expandedSections.features}>
          <Grid container spacing={2}>
            {features.map((feature, index) => (
              <Grid item xs={6} sm={4} key={index}>
                <Paper elevation={0} sx={{ 
                  p: 1, 
                  display: 'flex', 
                  alignItems: 'center',
                  backgroundColor: theme.palette.grey[100],
                  color: theme.palette.text.primary
                }}>
                  <FeatureIcon>
                    {feature.icon === 'bed' && <Bed fontSize="small" />}
                    {feature.icon === 'bath' && <Bathtub fontSize="small" />}
                    {feature.icon === 'garage' && <Garage fontSize="small" />}
                    {!feature.icon && <SquareFoot fontSize="small" />}
                  </FeatureIcon>
                  <Box>
                    <Typography variant="subtitle2">{feature.name}</Typography>
                    <Typography variant="body2" color={theme.palette.text.secondary}>
                      {feature.value || 'Sim'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Collapse>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <PremiumDialog 
        open={open} 
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth="lg"
      >
        <PremiumDialogTitle>
          <Box display="flex" alignItems="center">
            <Home sx={{ mr: 1 }} />
            {title}
          </Box>
          <IconButton onClick={onClose} color="inherit" size="small">
            <Close />
          </IconButton>
        </PremiumDialogTitle>
        
        <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default }}>
          <Box sx={{ pt: 2 }}>
            {renderBasicInfo()}
            {renderDescription()}
            {renderValues()}
            {renderAddress()}
            {renderFeatures()}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          justifyContent: 'flex-end',
          backgroundColor: theme.palette.background.default
        }}>
          <Button 
            onClick={onClose} 
            variant="contained" 
            color="primary"
            sx={{ 
              minWidth: '120px',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark
              }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </PremiumDialog>
    </ThemeProvider>
  );
};

export default ImovelDetailDialog;